<template>
  <div>
    <div
      class="container infobox-wrapper"
      v-if="showInfoBox"
    >
      <InfoBox
        :size="isMobile ? 'small' : 'medium'"
        class="w-100"
      >
        <div>
          <span class="text--semibold">{{ $t('navigation.infoBox') }}</span>
          <Anchor
            class="d-inline text--semibold"
            href="./biuletyn-biznesowy/finanse-i-ksiegowosc/e-fakturowanie-w-polsce"
          >
            {{ $t('navigation.infoBoxLink') }}
          </Anchor>
        </div>
        <Button
          :size="isMobile ? 'small' : 'medium'"
          class="ml-auto"
          data-test="close-navbar-info-box"
          icon="icon icon-cross"
          kind="icon-new"
          new-design
          no-border
          plain
          type="primary"
          @click="closeInfoBox"
        />
      </InfoBox>
    </div>

    <SubNavigation
      class="navbar__sub-navigation"
      v-if="!isInAppRoute || isInPromoRoute"
    />

    <SubscriptionBar v-if="isInAppRoute && isAuthorized" />

    <div
      class="navbar__content navbar__content--small-padding"
      v-show="hasTeleportedContent"
      id="navbar-custom-header"
    />

    <div
      :class="['navbar__content', { 'navbar__content--single-button': isAuthorized && !isInAppRoute, 'navbar__content--two-buttons': isAuthorized && isInAppRoute }]"
      v-if="!hasTeleportedContent"
    >
      <a
        href="/"
        v-if="!isAuthorized"
      >
        <img
          :src="require(`~/images/logo/fakturaonline-logo-${$i18n.locale}.svg`)"
          class="navbar__logo"
          :width="$t('navigation.logoWidth')"
          :height="$t('navigation.logoHeight')"
          :alt="$t('navigation.logoAlt')"
        >
        <img
          :src="require(`~/images/logo/fakturaonline-square-logo-${$i18n.locale}.svg`)"
          class="navbar__logo navbar__logo--mobile"
          width="48"
          height="48"
          :alt="$t('navigation.logoAlt')"
        >
      </a>

      <NavigationLinks class="hidden-md-down" />

      <ul class="navbar-buttons">
        <li v-if="isAuthorized && !isInAppRoute">
          <router-link
            :to="{ name: config.backButton.path }"
          >
            <Button
              icon="icon icon-dashboard"
              type="warning"
              new-design
              size="large"
            >
              {{ $t('navigation.backToApp') }}
            </Button>
          </router-link>
        </li>
        <li
          v-else
          class="navbar-buttons__button"
        >
          <CreateInvoiceButton />
        </li>
        <li
          v-if="!isAuthorized"
          class="navbar-buttons__button hidden-md-down"
        >
          <LoginButton />
        </li>
        <li
          v-if="!isAuthorized || isInAppRoute"
          class="navbar-buttons__button hidden-md-up"
        >
          <NavbarMenu />
        </li>
      </ul>
    </div>
    <Dialogs />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import Cookies from 'js-cookie';
import {
  PATH_ROOT,
} from '~/router';
import { navbarConfig } from '~/config/navbarConfig';
import { isInAppRoute, isPostsRoute, isInPromoRoute } from '~/lib/routerHelper';

import CreateInvoiceButton from '../header/CreateInvoiceButton.vue';
import LoginButton from '../header/LoginButton.vue';
import NavbarMenu from './NavbarMenu.vue';
import NavigationLinks from './NavigationLinks.vue';

export default {
  components: {
    CreateInvoiceButton,
    LoginButton,
    NavbarMenu,
    NavigationLinks,
    SubscriptionBar: () => import(/* webpackChunkName: "navigation" */ '~/modules/SubscriptionBar.vue'),
    Dialogs: () => import(/* webpackChunkName: "navigation" */ '~/modules/Dialogs.vue'),
    SubNavigation: () => import('./SubNavigation.vue'),
    InfoBox: () => import('~/components/InfoBox.vue'),
  },
  data: () => ({
    PATH_ROOT,
    hasTeleportedContent: false,
    isInfoBoxHidden: false,
  }),
  computed: {
    ...mapGetters('auth', [
      'isAuthorized',
    ]),
    isInAppRoute,
    isPostsRoute,
    isInPromoRoute,
    config() {
      return this.$i18n.locale === 'en' ? navbarConfig.usa : navbarConfig.default;
    },
    showInfoBox() {
      if (this.$i18n.locale !== 'pl') {
        return false;
      }

      const shouldShow = !this.isInAppRoute || (!this.isAuthorized && this.isInPromoRoute);
      return shouldShow && !this.isInfoBoxHidden;
    },
  },
  methods: {
    closeInfoBox() {
      this.isInfoBoxHidden = true;
      Cookies.set('navbar-infobox-closed', 'true', { expires: 3650 });
    },
    loadInfoBoxState() {
      const hideInfoBox = Cookies.get('navbar-infobox-closed');
      this.isInfoBoxHidden = hideInfoBox === 'true';
    },
  },
  mounted() {
    this.$emitter.on('element-teleported', (value) => { this.hasTeleportedContent = value; });
    this.loadInfoBoxState();
  },
  beforeDestroy() {
    this.$emitter.off('element-teleported');
  },
};
</script>

<style lang="scss" scoped>
.infobox-wrapper {
  @media only screen and (max-width: $--md - 1) {
    padding: 0 12px;
  }

  @media only screen and (max-width: $--xs - 1) {
    padding: 0 6px;
  }
}

.navbar__sub-navigation {
  @media only screen and (max-width: $--md - 1) {
    display: none;
  }
}

.navbar__content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: nowrap !important;
  padding-top: 16px;
  padding-bottom: 16px;

  @media only screen and (max-width: $--md - 1) {
    padding: 12px;
  }

  @media only screen and (max-width: $--sm - 1) {
    padding: 8px 12px;
  }

  @media only screen and (max-width: $--xs - 1) {
    padding: 8px 6px;
  }

  &--single-button {
    justify-content: flex-end;

    @media only screen and (max-width: $--sm - 1) {
      justify-content: center;
    }
  }

  &--two-buttons {
    @media only screen and (max-width: $--md - 1) {
      justify-content: flex-end;
    }
  }

  &--small-padding {
    min-height: 72px;
    padding-top: 3px;
    padding-bottom: 3px;

    @media only screen and (max-width: $--md - 1) {
      min-height: 64px;
      padding-top: 6px;
      padding-bottom: 6px;
    }
  }
}

.navbar__logo {
  display: block;
  max-width: 260px;

  @media only screen and (max-width: $--new-navbar-breakpoint) {
    max-width: 164px;
    display: none;
  }

  &--mobile {
    display: block;

    @media only screen and (min-width: ($--new-navbar-breakpoint + 1px)) {
      display: none;
    }
  }
}
</style>

<style lang="scss">
.navbar-buttons {
  display: flex;
  flex-wrap: nowrap;
  margin: 0;
  padding-left: 0;
  list-style: none;
}

.navbar-buttons__button {
  padding-left: 12px;

  @media only screen and (max-width: $--xs - 1) {
    padding-left: 4px;
  }
}
</style>
